import React, { useEffect, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Navigation, Pagination } from "swiper";
import { getTypicalProjectsByLanguage } from "../../../apis/typicalProjects";
import { useLanguage } from "../../../hooks/useLanguage";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

const isLongProjectTitle = (title = "") => title.trim().length > 42;

function TypicalProjectSlider({
  ecosystemGroupId: ecosystemGroupIdProp = "",
  variant = "default",
  showAllProjects = false,
}) {
  const { t } = useTranslation();
  const { currentLang } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ecosystemGroupId =
    ecosystemGroupIdProp || searchParams.get("ecosystemGroupId");
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const fetchProjects = async () => {
      setIsLoading(true);
      try {
        const data = await getTypicalProjectsByLanguage(currentLang);
        const rows = Array.isArray(data) ? data : [];
        const nextProjects =
          ecosystemGroupId && !showAllProjects
            ? rows.filter(
                (project) =>
                  String(project.ecosystemGroupId) === String(ecosystemGroupId),
              )
            : rows;

        if (!controller.signal.aborted) setProjects(nextProjects);
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Lỗi khi tải dự án tiêu biểu:", error);
          if (!controller.signal.aborted) setProjects([]);
        }
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    };

    fetchProjects();
    return () => controller.abort();
  }, [currentLang, ecosystemGroupId, showAllProjects]);

  if (isLoading) {
    return (
      <div
        className={`TypicalProjectSlider TypicalProjectSlider--${variant} TypicalProjectSlider--loading`}
        aria-label="Đang tải dự án"
      >
        {[0, 1].map((item) => (
          <div
            className="TypicalProjectSlider-skeleton"
            key={item}
            aria-hidden="true"
          />
        ))}
      </div>
    );
  }

  if (!projects.length) {
    return (
      <div
        className={`TypicalProjectSlider TypicalProjectSlider--${variant} TypicalProjectSlider--empty`}
      >
        Đang cập nhật dữ liệu dự án.
      </div>
    );
  }

  return (
    <div className={`TypicalProjectSlider TypicalProjectSlider--${variant}`}>
      <Swiper
        key={`${variant}-${currentLang}-${projects.length}`}
        slidesPerView={1}
        spaceBetween={24}
        pagination={{ clickable: true }}
        navigation={variant === "ecosystem"}
        loop={false}
        watchOverflow
        observer
        observeParents
        modules={[Navigation, Pagination]}
        className="mySwiper"
        breakpoints={{
          576: { slidesPerView: 1, spaceBetween: 16 },
          768: { slidesPerView: 2, spaceBetween: 24 },
          1200: { slidesPerView: 2, spaceBetween: 28 },
        }}
      >
        {projects.map((value) => {
          const longTitle = isLongProjectTitle(value.title || "");
          const rawUrl = value.websiteUrl || value.WebsiteUrl;
          const titleLower = (value.title || value.name || '').toLowerCase();
          const slugLower = (value.slug || value.path || '').toLowerCase();
          const websiteUrl = rawUrl || (
            (titleLower.includes('bắc mỹ') || slugLower.includes('bac-my')) ? 'https://benhvienbacmy.vn' :
            (titleLower.includes('đồng tân') || slugLower.includes('dong-tan')) ? 'https://benhviendongtan.vn' : null
          );
          const hasWebsite = Boolean(websiteUrl);
          const isEnglish = currentLang === "en";

          return (
            <SwiperSlide key={value.id}>
              <div
                className={`TypicalProjectSlider-content ${
                  longTitle ? "has-long-title" : ""
                } ${hasWebsite ? "has-website-url" : ""}`}
              >
                <img src={value.image} alt={value.title || "Dự án HSV"} />
                <div className="TypicalProjectSlider-panel">
                  <p className="TypicalProjectSlider-title">{value.title}</p>
                  
                  <div className="TypicalProjectSlider-actions">
                    {hasWebsite && (
                      <a
                        href={websiteUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="TypicalProjectSlider-action TypicalProjectSlider-action--website"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {isEnglish ? "Homepage" : "Xem Trang Chủ"}
                        <span aria-hidden="true">↗</span>
                      </a>
                    )}
                    <button
                      type="button"
                      className="TypicalProjectSlider-action TypicalProjectSlider-action--details"
                      onClick={() => navigate(`/du-an-tieu-bieu/${value.slug}`)}
                    >
                      {t("home.view_details")}
                      <span aria-hidden="true">{"\u2197\uFE0E"}</span>
                    </button>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>
    </div>
  );
}

export default TypicalProjectSlider;
